import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { apiService, Boleta } from '../services/api';
import Swal from 'sweetalert2';

interface DashboardHomeProps {
  user: any;
}

const DashboardHome: React.FC<DashboardHomeProps> = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [boletas, setBoletas] = useState<Boleta[]>([]);
  const [estadisticas, setEstadisticas] = useState({
    totalVentas: 0,
    cantidadVentas: 0,
    promedioVenta: 0,
    productosVendidos: 0
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar boletas del día
      const hoy = new Date();
      const yyyy = hoy.getFullYear();
      const mm = String(hoy.getMonth() + 1).padStart(2, '0');
      const dd = String(hoy.getDate()).padStart(2, '0');
      const hoyStr = `${yyyy}-${mm}-${dd}`;
      const response = await apiService.getBoletas({
        fecha_inicio: hoyStr,
        fecha_fin: hoyStr,
        per_page: 10
      });

      if (response.estado === 1 && response.registros) {
        setBoletas(response.registros);
        
        // Calcular estadísticas
        const ventas = response.registros;
        const totalVentas = ventas.reduce((sum, b) => sum + (b.total_boleta || 0), 0);
        const cantidadVentas = ventas.length;
        const promedioVenta = cantidadVentas > 0 ? totalVentas / cantidadVentas : 0;

        setEstadisticas({
          totalVentas,
          cantidadVentas,
          promedioVenta,
          productosVendidos: 0 // Se puede calcular con detalles
        });
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los datos del dashboard',
        confirmButtonColor: '#DC3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-CL');
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2 className="text-primary fw-bold">
            <i className="fas fa-tachometer-alt me-2"></i>
            Dashboard Principal
          </h2>
          <p className="text-muted mb-0">
            Bienvenido, <strong>{user?.nombre_usuario}</strong> | {new Date().toLocaleDateString('es-CL', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </Col>
        <Col xs="auto">
          <Button 
            variant="outline-primary" 
            onClick={cargarDatos}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
            Actualizar
          </Button>
        </Col>
      </Row>

      {/* Tarjetas de estadísticas */}
      <Row className="mb-4">
        <Col md={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <div className="bg-primary rounded-3 p-3">
                  <i className="fa-solid fa-dollar-sign text-white fa-2x"></i>
                </div>
              </div>
              <div className="flex-grow-1 ms-3">
                <h5 className="text-muted mb-1">Ventas del Día</h5>
                <h3 className="fw-bold text-primary mb-0">{formatCurrency(estadisticas.totalVentas)}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <div className="bg-success rounded-3 p-3">
                  <i className="fa-solid fa-receipt text-white fa-2x"></i>
                </div>
              </div>
              <div className="flex-grow-1 ms-3">
                <h5 className="text-muted mb-1">Cantidad Ventas</h5>
                <h3 className="fw-bold text-success mb-0">{estadisticas.cantidadVentas}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <div className="bg-info rounded-3 p-3">
                  <i className="fa-solid fa-chart-line text-white fa-2x"></i>
                </div>
              </div>
              <div className="flex-grow-1 ms-3">
                <h5 className="text-muted mb-1">Promedio Venta</h5>
                <h3 className="fw-bold text-info mb-0">{formatCurrency(estadisticas.promedioVenta)}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <div className="bg-warning rounded-3 p-3">
                  <i className="fa-solid fa-box text-dark fa-2x"></i>
                </div>
              </div>
              <div className="flex-grow-1 ms-3">
                <h5 className="text-muted mb-1">Productos Vendidos</h5>
                <h3 className="fw-bold text-warning mb-0">{estadisticas.productosVendidos}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Accesos rápidos */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-bolt me-2"></i>
                Accesos Rápidos
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4} className="mb-2">
                  <Link to="/punto-venta" className="btn btn-outline-primary w-100">
                    <i className="fas fa-cash-register me-2"></i>
                    Nueva Venta
                  </Link>
                </Col>
                <Col md={4} className="mb-2">
                  <Link to="/reportes" className="btn btn-outline-info w-100">
                    <i className="fas fa-chart-bar me-2"></i>
                    Ver Reportes
                  </Link>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Últimas ventas */}
      <Row>
        <Col>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <i className="fas fa-history me-2"></i>
                Últimas Ventas del Día
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Boleta #</th>
                    <th>Fecha/Hora</th>
                    <th>Cliente</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {boletas.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No hay ventas registradas hoy
                      </td>
                    </tr>
                  ) : (
                    boletas.map((boleta) => (
                      <tr key={boleta.id_boleta}>
                        <td className="fw-semibold">#{boleta.id_boleta}</td>
                        <td>{formatDate(boleta.fecha_ingreso_boleta)}</td>
                        <td>{boleta.nombre_cliente_boleta || 'Cliente'}</td>
                        <td className="fw-bold text-primary">
                          {formatCurrency(boleta.total_boleta)}
                        </td>
                        <td>
                          <Badge 
                            bg={boleta.id_estado_boleta === 1 ? 'success' : 'secondary'}
                            className="px-2 py-1"
                          >
                            {boleta.id_estado_boleta === 1 ? 'Activa' : 'Anulada'}
                          </Badge>
                        </td>
                        <td>
                          <Link 
                            to={`/boletas/${boleta.id_boleta}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="fas fa-eye me-1"></i>
                            Ver
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DashboardHome;
